import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { BookOutlined, EyeOutlined, SaveOutlined } from '@ant-design/icons';
import type { KrybotAgent } from '../../../types/krybot';
import { resolveAgentSkillBundles } from '../../../services/api/krybotService';
import { krysalisBridgeCatalog } from '../../../catalog/krysalisBridgeCatalog';
import {
  extractReferencePathsFromSkillMarkdown,
  patchCapabilitiesSkillBinding,
  readCapabilitiesSkillBinding,
} from '../../../utils/agentSkillBinding';
import { useAgentSkillModal } from './useAgentSkillModal';
import { AgentSkillContentModal } from './AgentSkillContentModal';
import { AgentSkillVerifyPanel } from './AgentSkillVerifyPanel';

const { Text, Paragraph } = Typography;

interface AgentSkillsResourcesTabProps {
  agent: KrybotAgent | null;
  agentData: Partial<KrybotAgent>;
  isCreating: boolean;
  onDataChange: (updates: Partial<KrybotAgent>) => void;
  onSave: () => void;
  loading: boolean;
}

export const AgentSkillsResourcesTab: React.FC<AgentSkillsResourcesTabProps> = ({
  agent,
  agentData,
  onDataChange,
  onSave,
  loading,
}) => {
  const binding = readCapabilitiesSkillBinding(
    agentData.capabilities as Record<string, unknown> | undefined
  );
  const [skillBundle, setSkillBundle] = useState(binding.skill_bundle);
  const [bridgeId, setBridgeId] = useState(binding.bridge_krybot_agent_id);
  const [includeSkill, setIncludeSkill] = useState(binding.include_skill_file_in_prompt);
  const [referencePaths, setReferencePaths] = useState<string[]>([]);

  useEffect(() => {
    const next = readCapabilitiesSkillBinding(
      agentData.capabilities as Record<string, unknown> | undefined
    );
    setSkillBundle(next.skill_bundle);
    setBridgeId(next.bridge_krybot_agent_id);
    setIncludeSkill(next.include_skill_file_in_prompt);
  }, [agentData.capabilities]);

  const resolvedBundles = useMemo(
    () =>
      resolveAgentSkillBundles({
        capabilities: agentData.capabilities as Record<string, unknown> | undefined,
        default_context: agentData.default_context as Record<string, unknown> | undefined,
      }),
    [agentData.capabilities, agentData.default_context]
  );

  const skillModal = useAgentSkillModal(resolvedBundles);

  const bridgeOptions = useMemo(
    () =>
      krysalisBridgeCatalog.krybot_agents.map((row) => ({
        value: row.id,
        label: `${row.id} → ${row.skill_bundle}`,
      })),
    []
  );

  const applyBindingToDraft = (next: {
    skill_bundle?: string;
    bridge_krybot_agent_id?: string;
    include_skill_file_in_prompt?: boolean;
  }) => {
    const capabilities = patchCapabilitiesSkillBinding(
      agentData.capabilities as Record<string, unknown> | undefined,
      next
    );
    onDataChange({ capabilities });
  };

  const flushBinding = (): Record<string, unknown> => {
    const capabilities = patchCapabilitiesSkillBinding(
      agentData.capabilities as Record<string, unknown> | undefined,
      {
        skill_bundle: skillBundle,
        bridge_krybot_agent_id: bridgeId,
        include_skill_file_in_prompt: includeSkill,
      }
    );
    onDataChange({ capabilities });
    return capabilities;
  };

  const handleSave = () => {
    flushBinding();
    onSave();
  };

  const handlePreview = () => {
    const capabilities = flushBinding();
    const bundles = resolveAgentSkillBundles({
      capabilities,
      default_context: agentData.default_context as Record<string, unknown> | undefined,
    });
    void skillModal.openSkillModal(bundles);
  };

  useEffect(() => {
    if (skillModal.skillBody) {
      setReferencePaths(extractReferencePathsFromSkillMarkdown(skillModal.skillBody));
    }
  }, [skillModal.skillBody]);

  const displayName = agent?.display_name || agent?.name || 'New agent';

  return (
    <div className="agent-skills-resources-tab" data-testid="agent-skills-resources-tab">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ margin: 0 }}>
          <BookOutlined /> Skills &amp; resources
        </h3>
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => void handlePreview()} disabled={loading}>
            Preview SKILL
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={loading}>
            Save Changes
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Management bind (capabilities)"
        description={
          <span>
            Skill linkage is stored on <Text code>capabilities.skill_bundle</Text> and optional{' '}
            <Text code>bridge_krybot_agent_id</Text> (not boolean capability flags). Boolean flags stay
            on the Capabilities tab. Deploy registry (<Text code>ai-rp-*</Text> /{' '}
            <Text code>co-pa-*</Text>) is read-only elsewhere — this tab does not write deploy_state.
          </span>
        }
      />

      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Agent: <Text strong>{displayName}</Text>
        {resolvedBundles.length > 0 ? (
          <>
            {' '}
            · Resolved bundles:{' '}
            {resolvedBundles.map((b) => (
              <Tag key={b} color="blue">
                {b}
              </Tag>
            ))}
          </>
        ) : (
          <> · No skill bundles resolved yet</>
        )}
      </Paragraph>

      <Form layout="vertical" style={{ maxWidth: 720 }}>
        <Form.Item
          label="skill_bundle"
          extra="Kryspace package slug (e.g. krysalis-bridge-session). Canonical write under capabilities."
        >
          <Input
            value={skillBundle}
            placeholder="krysalis-…"
            onChange={(e) => {
              const next = e.target.value;
              setSkillBundle(next);
              applyBindingToDraft({ skill_bundle: next });
            }}
          />
        </Form.Item>

        <Form.Item
          label="bridge_krybot_agent_id"
          extra="Catalog constant (KRYBOT_*). Resolves to a skill_bundle via krysalis_bridge_catalog."
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Select catalog agent id"
            value={bridgeId || undefined}
            options={bridgeOptions}
            onChange={(v) => {
              const next = typeof v === 'string' ? v : '';
              setBridgeId(next);
              applyBindingToDraft({ bridge_krybot_agent_id: next });
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="Include SKILL file in prompt">
          <Switch
            checked={includeSkill}
            onChange={(checked) => {
              setIncludeSkill(checked);
              applyBindingToDraft({ include_skill_file_in_prompt: checked });
            }}
          />
          <Text type="secondary" style={{ marginLeft: 12 }}>
            When off, sets <Text code>include_skill_file_in_prompt: false</Text>
          </Text>
        </Form.Item>
      </Form>

      {(skillModal.skillPaths.length > 0 || referencePaths.length > 0) && (
        <div style={{ marginTop: 8 }}>
          <Text strong>Skill paths / references</Text>
          <ul style={{ marginTop: 8 }}>
            {skillModal.skillPaths.map((p) => (
              <li key={`path-${p}`}>
                <Text code>{p}</Text>
              </li>
            ))}
            {referencePaths.map((p) => (
              <li key={`ref-${p}`}>
                <Text type="secondary">ref:</Text> <Text code>{p}</Text>
              </li>
            ))}
          </ul>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Paths refresh after Preview SKILL. Reference rows are parsed from the SKILL.md “Reference
            Files” section when present.
          </Paragraph>
        </div>
      )}

      {resolvedBundles.length === 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="No skill bound"
          description="Set a skill_bundle and/or bridge_krybot_agent_id, then Save. Legacy keys in default_context still resolve for preview but new edits write to capabilities."
        />
      )}

      <AgentSkillVerifyPanel skillBundles={resolvedBundles} />

      <AgentSkillContentModal
        open={skillModal.skillModalOpen}
        title={`SKILL — ${displayName}`}
        onCancel={() => skillModal.setSkillModalOpen(false)}
        skillBundles={resolvedBundles}
        selectedSkillBundle={skillModal.selectedSkillBundle}
        onSelectBundle={skillModal.handleSkillBundleSelect}
        skillLoading={skillModal.skillLoading}
        skillBody={skillModal.skillBody}
        skillPaths={skillModal.skillPaths}
        skillTruncated={skillModal.skillTruncated}
      />
    </div>
  );
};
